<script>
import { submitShopApply } from "@/api/shopApply"

const LICENSE_FIELDS = Object.freeze([
    {
        field: "business_license",
        label: "营业执照",
        description: "请上传清晰完整的营业执照原件照片"
    },
    {
        field: "drug_business_license",
        label: "药品经营许可证",
        description: "请上传在有效期内的药品经营许可证"
    }
])

const UPLOAD_SUCCESS_CODES = [0, 10068]

const createInitialForm = () => ({
    company_name: "",
    company_address: "",
    contact_name: "",
    phone: "",
    business_license: "",
    drug_business_license: ""
})

const createUploadState = () => ({
    business_license: false,
    drug_business_license: false
})

export default {
    name: "MerchantApplyDialog",
    props: {
        visible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        const validateMobile = (rule, value, callback) => {
            if (!/^1[3-9]\d{9}$/.test(value)) {
                callback(new Error("请输入正确的联系电话"))
                return
            }
            callback()
        }

        return {
            form: createInitialForm(),
            licenseFields: LICENSE_FIELDS,
            uploading: createUploadState(),
            submitting: false,
            rules: {
                company_name: [
                    { required: true, message: "请输入公司名称", trigger: "blur" },
                    { max: 100, message: "公司名称不能超过100个字符", trigger: "blur" }
                ],
                company_address: [
                    { required: true, message: "请输入公司地址", trigger: "blur" },
                    { max: 200, message: "公司地址不能超过200个字符", trigger: "blur" }
                ],
                contact_name: [
                    { required: true, message: "请输入联系人", trigger: "blur" },
                    { max: 30, message: "联系人不能超过30个字符", trigger: "blur" }
                ],
                phone: [
                    { required: true, message: "请输入联系电话", trigger: "blur" },
                    { validator: validateMobile, trigger: "blur" }
                ],
                business_license: [
                    { required: true, message: "请上传营业执照", trigger: "change" }
                ],
                drug_business_license: [
                    { required: true, message: "请上传药品经营许可证", trigger: "change" }
                ]
            }
        }
    },
    computed: {
        uploadAction() {
            return this.$config.baseUrl + "api/i/image"
        },
        isUploading() {
            return Object.keys(this.uploading).some(field => this.uploading[field])
        }
    },
    methods: {
        handleCancel() {
            if (this.submitting) return
            this.$emit("update:visible", false)
        },
        handleClosed() {
            this.form = createInitialForm()
            this.uploading = createUploadState()
            this.$nextTick(() => {
                if (this.$refs.applyForm) this.$refs.applyForm.clearValidate()
            })
        },
        beforeUpload(field, file) {
            const allowedTypes = ["image/jpeg", "image/png"]
            const isAllowedType = allowedTypes.includes(file.type)

            if (!isAllowedType) {
                this.$message.error("仅支持 JPG、JPEG、PNG 格式的图片")
            }

            if (isAllowedType) this.uploading[field] = true
            return isAllowedType
        },
        handleUploadSuccess(field, response) {
            this.uploading[field] = false

            const imagePath = response && response.data && response.data.pic_path
            const responseCode = Number(response && response.code)
            const isUploadSuccess = UPLOAD_SUCCESS_CODES.includes(responseCode)
            if (!isUploadSuccess || !imagePath) {
                this.$message.error((response && response.message) || "图片上传失败，请重试")
                return
            }

            // 上传接口以 10068 表示成功，pic_path 可直接用于回显和提交。
            this.form[field] = imagePath
            this.$nextTick(() => {
                if (this.$refs.applyForm) this.$refs.applyForm.clearValidate(field)
            })
        },
        handleUploadError(field) {
            this.uploading[field] = false
            this.$message.error("图片上传失败，请检查网络后重试")
        },
        removeImage(field) {
            this.form[field] = ""
            this.$nextTick(() => {
                if (this.$refs.applyForm) this.$refs.applyForm.validateField(field)
            })
        },
        handleSubmit() {
            if (this.isUploading) {
                this.$message.warning("图片正在上传，请稍候")
                return
            }

            this.$refs.applyForm.validate(async valid => {
                if (!valid || this.submitting) return

                this.submitting = true
                try {
                    const response = await submitShopApply({
                        company_name: this.form.company_name,
                        company_address: this.form.company_address,
                        contact_name: this.form.contact_name,
                        phone: this.form.phone,
                        business_license: this.form.business_license,
                        drug_business_license: this.form.drug_business_license
                    })
                    if (response.code !== 0) {
                        this.$message.error(response.message || "入驻申请提交失败，请重试")
                        return
                    }

                    this.$emit("success", response.data)
                    this.$emit("update:visible", false)
                } catch (error) {
                    this.$message.error(error.message || "入驻申请提交失败，请重试")
                } finally {
                    this.submitting = false
                }
            })
        }
    }
}
</script>

<template>
    <el-dialog
        class="merchant-apply-dialog"
        title="商家入驻申请"
        :visible="visible"
        width="860px"
        top="5vh"
        :close-on-click-modal="false"
        :close-on-press-escape="!submitting"
        :show-close="!submitting"
        append-to-body
        @close="handleCancel"
        @closed="handleClosed"
    >
        <div slot="title" class="dialog-heading">
            <span class="dialog-heading__icon" aria-hidden="true">
                <i class="el-icon-office-building"></i>
            </span>
            <div class="dialog-heading__content">
                <h2 class="dialog-heading__title">商家入驻申请</h2>
                <p class="dialog-heading__description">完善企业信息并提交资质，我们将在 1–3 个工作日内完成审核</p>
            </div>
        </div>

        <div class="review-notice" role="note">
            <div class="review-notice__item">
                <i class="el-icon-lock" aria-hidden="true"></i>
                <span>资料仅用于平台入驻审核</span>
            </div>
            <div class="review-notice__item">
                <i class="el-icon-circle-check" aria-hidden="true"></i>
                <span>请确保信息真实、证照清晰且在有效期内</span>
            </div>
        </div>

        <el-form
            ref="applyForm"
            class="apply-form"
            :model="form"
            :rules="rules"
            label-position="top"
        >
            <section class="form-section" aria-labelledby="company-info-title">
                <header class="section-heading">
                    <span class="section-heading__index">01</span>
                    <div>
                        <h3 id="company-info-title" class="section-heading__title">企业与联系人</h3>
                        <p class="section-heading__description">请填写营业执照登记信息及便于联系的负责人信息</p>
                    </div>
                </header>

                <div class="form-grid">
                    <el-form-item label="公司名称" prop="company_name">
                        <el-input
                            v-model.trim="form.company_name"
                            maxlength="100"
                            placeholder="请输入营业执照上的公司全称"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="公司地址" prop="company_address">
                        <el-input
                            v-model.trim="form.company_address"
                            maxlength="200"
                            placeholder="请输入公司详细地址"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="联系人" prop="contact_name">
                        <el-input
                            v-model.trim="form.contact_name"
                            maxlength="30"
                            placeholder="请输入联系人姓名"
                            clearable
                        />
                    </el-form-item>
                    <el-form-item label="联系电话" prop="phone">
                        <el-input
                            v-model.trim="form.phone"
                            maxlength="11"
                            placeholder="请输入11位手机号码"
                            clearable
                        />
                    </el-form-item>
                </div>
            </section>

            <section class="form-section form-section--license" aria-labelledby="license-info-title">
                <header class="section-heading">
                    <span class="section-heading__index">02</span>
                    <div>
                        <h3 id="license-info-title" class="section-heading__title">企业资质</h3>
                        <p class="section-heading__description">支持 JPG、JPEG、PNG 格式，文件不要过大</p>
                    </div>
                </header>

                <div class="license-grid">
                    <el-form-item
                        v-for="license in licenseFields"
                        :key="license.field"
                        class="license-form-item"
                        :label="license.label"
                        :prop="license.field"
                    >
                        <div
                            v-loading="uploading[license.field]"
                            class="upload-frame"
                            :class="{ 'is-uploaded': form[license.field] }"
                        >
                            <el-upload
                                class="license-uploader"
                                :action="uploadAction"
                                :show-file-list="false"
                                accept=".jpg,.jpeg,.png"
                                :aria-label="`上传${license.label}`"
                                :before-upload="file => beforeUpload(license.field, file)"
                                :on-success="response => handleUploadSuccess(license.field, response)"
                                :on-error="() => handleUploadError(license.field)"
                            >
                                <div v-if="form[license.field]" class="license-preview">
                                    <img :src="form[license.field]" :alt="`${license.label}预览`" />
                                    <div class="preview-status">
                                        <i class="el-icon-circle-check" aria-hidden="true"></i>
                                        <span>已上传</span>
                                    </div>
                                    <div class="preview-replace">
                                        <i class="el-icon-refresh" aria-hidden="true"></i>
                                        <span>点击更换图片</span>
                                    </div>
                                </div>
                                <div v-else class="upload-placeholder">
                                    <span class="upload-placeholder__icon" aria-hidden="true">
                                        <i class="el-icon-upload2"></i>
                                    </span>
                                    <strong>点击上传{{ license.label }}</strong>
                                    <span>{{ license.description }}</span>
                                </div>
                            </el-upload>
                            <el-button
                                v-if="form[license.field]"
                                class="remove-image-button"
                                type="text"
                                icon="el-icon-delete"
                                :aria-label="`删除${license.label}`"
                                @click.stop="removeImage(license.field)"
                            >删除</el-button>
                        </div>
                    </el-form-item>
                </div>
            </section>
        </el-form>

        <div slot="footer" class="dialog-footer">
            <p class="dialog-footer__hint">
                <i class="el-icon-info" aria-hidden="true"></i>
                提交即表示您确认所填资料真实有效
            </p>
            <div class="dialog-footer__actions">
                <el-button :disabled="submitting" @click="handleCancel">暂不申请</el-button>
                <el-button
                    type="primary"
                    :loading="submitting"
                    :disabled="isUploading"
                    @click="handleSubmit"
                >提交入驻申请</el-button>
            </div>
        </div>
    </el-dialog>
</template>

<style lang="scss">
.merchant-apply-dialog {
    .el-dialog {
        max-width: calc(100% - 32px);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(31, 35, 48, 0.16);
    }

    .el-dialog__header {
        padding: 22px 28px;
        border-bottom: 1px solid #ebeef2;
        background: linear-gradient(135deg, #fff 0%, #fff8fa 100%);
    }

    .el-dialog__headerbtn {
        top: 24px;
        right: 24px;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        transition: background-color 0.2s ease;

        &:hover,
        &:focus {
            background: #f4f5f7;
        }
    }

    .el-dialog__body {
        max-height: calc(90vh - 174px);
        box-sizing: border-box;
        padding: 20px 28px 24px;
        overflow-y: auto;
        background: #f7f8fa;
    }

    .el-dialog__footer {
        padding: 16px 28px;
        border-top: 1px solid #ebeef2;
        background: #fff;
    }

    .dialog-heading {
        display: flex;
        align-items: center;
        padding-right: 48px;
    }

    .dialog-heading__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        margin-right: 14px;
        flex-shrink: 0;
        border-radius: 10px;
        background: $base-color;
        color: #fff;
        box-shadow: 0 8px 18px rgba(255, 84, 123, 0.22);
        font-size: 21px;
    }

    .dialog-heading__content {
        min-width: 0;
    }

    .dialog-heading__title {
        margin: 0;
        color: #242832;
        font-size: 20px;
        font-weight: 600;
        line-height: 1.35;
    }

    .dialog-heading__description {
        margin: 4px 0 0;
        color: #737985;
        font-size: 13px;
        line-height: 1.5;
    }

    .review-notice {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        margin-bottom: 16px;
        padding: 11px 16px;
        border: 1px solid #f7dbe2;
        border-radius: 8px;
        background: #fff8fa;
        color: #626975;
        font-size: 12px;
    }

    .review-notice__item {
        display: flex;
        align-items: center;
        min-width: 0;

        i {
            margin-right: 7px;
            color: $base-color;
            font-size: 15px;
        }
    }

    .form-section {
        padding: 20px 22px 4px;
        border: 1px solid #ebeef2;
        border-radius: 10px;
        background: #fff;

        & + .form-section {
            margin-top: 16px;
        }
    }

    .form-section--license {
        padding-bottom: 6px;
    }

    .section-heading {
        display: flex;
        align-items: flex-start;
        margin-bottom: 18px;
    }

    .section-heading__index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        margin-right: 10px;
        flex-shrink: 0;
        border-radius: 8px;
        background: #fff0f4;
        color: $base-color;
        font-size: 12px;
        font-weight: 600;
    }

    .section-heading__title {
        margin: 0;
        color: #303540;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.4;
    }

    .section-heading__description {
        margin: 3px 0 0;
        color: #8a909c;
        font-size: 12px;
        line-height: 1.5;
    }

    .form-grid,
    .license-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 18px;
    }

    .el-form-item {
        min-width: 0;
        margin-bottom: 18px;
    }

    .el-form-item__label {
        padding-bottom: 7px;
        color: #3f4550;
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
    }

    .el-input__inner {
        height: 40px;
        border-color: #dfe3e9;
        border-radius: 6px;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;

        &:hover {
            border-color: #c8cdd5;
        }

        &:focus {
            border-color: $base-color;
            box-shadow: 0 0 0 3px rgba(255, 84, 123, 0.1);
        }
    }

    .license-form-item {
        margin-bottom: 16px;
    }

    .upload-frame {
        position: relative;
        height: 180px;
        box-sizing: border-box;
        border: 1px dashed #cfd4dc;
        border-radius: 8px;
        overflow: hidden;
        background: #fafbfc;
        transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;

        &:hover,
        &:focus-within {
            border-color: $base-color;
            background: #fffafb;
            box-shadow: 0 0 0 3px rgba(255, 84, 123, 0.08);
        }

        &.is-uploaded {
            border-style: solid;
            border-color: #dfe3e9;
            background: #fff;
        }
    }

    .license-uploader,
    .license-uploader .el-upload,
    .upload-placeholder,
    .license-preview {
        width: 100%;
        height: 100%;
    }

    .license-uploader .el-upload {
        display: block;
        text-align: left;
        cursor: pointer;
    }

    .upload-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        padding: 22px;
        text-align: center;

        strong {
            margin-top: 10px;
            color: #3e4450;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.4;
        }

        > span:last-child {
            max-width: 250px;
            margin-top: 6px;
            color: #969ca7;
            font-size: 12px;
            line-height: 1.5;
        }
    }

    .upload-placeholder__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        background: #fff0f4;
        color: $base-color;
        font-size: 21px;
    }

    .license-preview {
        position: relative;
        box-sizing: border-box;
        padding: 10px;

        img {
            display: block;
            width: 100%;
            height: 100%;
            border-radius: 5px;
            object-fit: contain;
            background: #f4f5f7;
        }
    }

    .preview-status {
        position: absolute;
        top: 16px;
        left: 16px;
        display: inline-flex;
        align-items: center;
        padding: 5px 9px;
        border-radius: 14px;
        background: rgba(34, 148, 83, 0.92);
        color: #fff;
        font-size: 12px;
        line-height: 1;

        i {
            margin-right: 4px;
        }
    }

    .preview-replace {
        position: absolute;
        right: 10px;
        bottom: 10px;
        left: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 38px;
        border-radius: 0 0 5px 5px;
        background: rgba(22, 25, 31, 0.68);
        color: #fff;
        font-size: 12px;
        opacity: 0;
        transition: opacity 0.2s ease;

        i {
            margin-right: 6px;
        }
    }

    .is-uploaded:hover .preview-replace,
    .is-uploaded:focus-within .preview-replace {
        opacity: 1;
    }

    .remove-image-button {
        position: absolute;
        top: 12px;
        right: 14px;
        z-index: 2;
        padding: 5px 8px;
        border-radius: 4px;
        background: rgba(255, 255, 255, 0.95);
        color: #d14343;
        box-shadow: 0 2px 8px rgba(31, 35, 48, 0.12);

        &:hover,
        &:focus {
            background: #fff;
            color: #b92d2d;
        }
    }

    .dialog-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
    }

    .dialog-footer__hint {
        display: flex;
        align-items: center;
        margin: 0;
        color: #8a909c;
        font-size: 12px;
        line-height: 1.5;
        text-align: left;

        i {
            margin-right: 6px;
            color: #a7acb5;
        }
    }

    .dialog-footer__actions {
        display: flex;
        align-items: center;
        flex-shrink: 0;

        .el-button {
            min-width: 104px;
            height: 40px;
            border-radius: 6px;
        }

        .el-button--primary {
            min-width: 128px;
        }
    }
}

@media (max-width: 767px) {
    .merchant-apply-dialog {
        .el-dialog__header,
        .el-dialog__body,
        .el-dialog__footer {
            padding-right: 18px;
            padding-left: 18px;
        }

        .dialog-heading__description,
        .dialog-footer__hint {
            display: none;
        }

        .review-notice,
        .dialog-footer {
            align-items: stretch;
            flex-direction: column;
        }

        .form-grid,
        .license-grid {
            grid-template-columns: 1fr;
        }

        .dialog-footer__actions {
            justify-content: flex-end;
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .merchant-apply-dialog * {
        transition-duration: 0.01ms !important;
    }
}
</style>
